import { useQuery, useSuspenseQuery } from "@apollo/client/react";
import type { PaginationState } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";

import type { ListProps } from "~/common/functions/listProps";
import type { EvidencesBoolExp, InputMaybe } from "~/common/types";
import {
  GetEvidenceSetDetailDocument,
  type GetEvidenceSetDetailQuery,
  type GetEvidenceSetDetailQueryVariables,
  GetEvidenceSetEvidencesDocument,
  type GetEvidenceSetEvidencesQuery,
  type GetEvidenceSetEvidencesQueryVariables,
} from "~/features/dataImportProjects/gqls";
import { flattenEvidence } from "~/features/dataImportProjects/utils/evidenceTransform";
import { useTenant } from "~/features/tenant/context";
import { HasuraGraphQLQueryMapper } from "~/lib/hasura";

const generateWhere = (evidenceSetId: string): InputMaybe<EvidencesBoolExp> => ({
  evidenceEvidenceSets: { evidenceSetId: { _eq: evidenceSetId } },
});

const EXCLUDED_COLUMNS = ["__typename"] as const;

export function useEvidencesEditTable(evidenceSetId: string) {
  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }
  const { data: evidenceSetData } = useSuspenseQuery<GetEvidenceSetDetailQuery, GetEvidenceSetDetailQueryVariables>(
    GetEvidenceSetDetailDocument,
    {
      variables: { tenantId, id: evidenceSetId },
    },
  );

  const evidenceSet = evidenceSetData?.evidenceSets?.[0];
  const schemas = evidenceSet?.evidenceSetPropertySchemaSet?.propertySchemaSet.propertySchemaAssociations;

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });

  const [showAllFields, setShowAllFields] = useState(false);

  const listProps = useMemo<ListProps>(
    () => ({
      page: pagination.pageIndex,
      rowsPerPage: pagination.pageSize,
      sortOrder: {
        name: "id",
        direction: "asc",
      },
    }),
    [pagination.pageIndex, pagination.pageSize],
  );

  const { data } = useQuery<GetEvidenceSetEvidencesQuery, GetEvidenceSetEvidencesQueryVariables>(
    GetEvidenceSetEvidencesDocument,
    {
      variables: {
        tenantId,
        where: generateWhere(evidenceSetId),
        ...HasuraGraphQLQueryMapper.list.toQueryArgs(listProps),
      },
    },
  );

  const evidences = data?.evidences ?? [];
  const evidencesCount = data?.evidencesAggregate.aggregate?.count ?? 0;

  const flattenedDrafts = useMemo(() => evidences.map(flattenEvidence), [evidences]);

  const columnHeaders = useMemo(() => {
    const headers = new Map<string, string>();

    schemas?.forEach((schema) => {
      const key = `properties/${schema.name}`;
      headers.set(key, key);
    });

    flattenedDrafts.forEach((draft) => {
      Object.keys(draft)
        .filter((key) => !EXCLUDED_COLUMNS.includes(key as (typeof EXCLUDED_COLUMNS)[number]))
        .filter((key) => (showAllFields ? true : !key.startsWith("metadata/")))
        .forEach((key) => {
          if (!headers.has(key)) {
            headers.set(key, key);
          }
        });
    });

    return headers;
  }, [schemas, flattenedDrafts, showAllFields]);

  const handleShowAllFieldsChange = useCallback((next: boolean) => {
    setShowAllFields(next);
  }, []);

  return {
    pagination,
    setPagination,
    flattenedDrafts,
    columnHeaders,
    evidencesCount,
    showAllFields,
    setShowAllFields: handleShowAllFieldsChange,
  };
}
